import type { Draft } from 'immer'
import entries from 'lodash/entries'
import isArray from 'lodash/isArray'
import isObject from 'lodash/isObject'
import values from 'lodash/values'

export type EntityFormErrorMap = Record<string, string | undefined>

export function hasEntityFormErrors(errors: EntityFormErrorMap): boolean {
  return values(errors).some(Boolean)
}

export function entityFormErrorEntries(errors: EntityFormErrorMap): Array<{ path: string; message: string }> {
  return entries(errors).flatMap(([path, message]) => (message ? [{ path, message }] : []))
}

type NestedFormConfig<NestedKey extends string> = {
  nestedKey?: NestedKey
}

export function createEntityFormStateHelpers<
  Values extends Record<string, unknown>,
  Dirty extends Record<string, unknown>,
  NestedKey extends string = 'address'
>(config: NestedFormConfig<NestedKey> = {}) {
  const nestedKey = (config.nestedKey ?? ('address' as NestedKey)) as NestedKey
  const prefix = `${nestedKey}.`

  type NestedValues = Values[NestedKey & keyof Values]
  type TopLevelField = Extract<Exclude<keyof Values, NestedKey>, string>
  type NestedField = NestedValues extends Record<string, unknown> ? Extract<keyof NestedValues, string> : string
  type FieldPath = TopLevelField | `${NestedKey}.${NestedField}`
  type FieldValue =
    | (TopLevelField extends keyof Values ? Values[TopLevelField] : never)
    | (NestedValues extends Record<string, unknown>
        ? NestedField extends keyof NestedValues
          ? NestedValues[NestedField]
          : never
        : never)

  function markDirty(draft: Draft<Dirty>, path: FieldPath): void {
    if (path.startsWith(prefix)) {
      const key = path.slice(prefix.length)
      const current = (draft as Record<string, unknown>)[nestedKey]
      if (current !== true && (current == null || !isObject(current))) {
        ;(draft as Record<string, unknown>)[nestedKey] = {}
      }
      const nested = (draft as Record<string, unknown>)[nestedKey]
      if (nested !== true && isObject(nested)) {
        ;(nested as Record<string, boolean>)[key] = true
      }
      return
    }
    ;(draft as Record<string, unknown>)[path as string] = true
  }

  function markDirtyPaths(draft: Draft<Dirty>, paths: FieldPath | readonly FieldPath[]): void {
    const list = isArray(paths) ? paths : [paths]
    for (const path of list) {
      markDirty(draft, path)
    }
  }

  function setField(draft: Draft<Values>, path: FieldPath, value: FieldValue): void {
    if (path.startsWith(prefix)) {
      const key = path.slice(prefix.length)
      const nested = (draft as Record<string, unknown>)[nestedKey]
      if (isObject(nested)) {
        ;(nested as Record<string, unknown>)[key] = value
      }
      return
    }
    ;(draft as Record<string, unknown>)[path as string] = value
  }

  function clearErrors(errors: EntityFormErrorMap, paths: FieldPath | readonly FieldPath[]): EntityFormErrorMap {
    const next = { ...errors }
    delete next.root
    const list = isArray(paths) ? paths : [paths]
    for (const path of list) {
      delete next[path as string]
    }
    return next
  }

  return {
    markDirty,
    markDirtyPaths,
    setField,
    clearErrors
  }
}
